import React, {useCallback, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Divider, Flex, Skeleton, Stack, Text, useDisclosure} from '@chakra-ui/react'
import ProductSetCockpitTotal from './product-set-cockpit-total'
import {FormattedMessage} from 'react-intl'
import ProductSetModal from './product-set-modal'
import ProductSetTile from '../../../components/product-set-tile'
import {trackPsdpClickProductTile} from '../../../tracking/analytics'
import ProductSpecialOffers from '../../../components/special-offer/product-special-offers'
import CockpitOrderSection from './cockpit-order-section'
import useMultiSite from '../../../hooks/use-multi-site'
import AddToCartCTA from '../../../components/add-to-cart-cta'
import WhereToBuyCTA from '../../../components/where-to-buy-cta'
import CompareCta from './compare-cta'
import PayLater from '../../../components/pay-later'
import useProductPricing from '../../../hooks/use-product-pricing'
import QuantityPicker from '../../../components/quantity-picker'

const PsdpCockpitRightColumn = ({
    derivedProduct,
    isGetPropsLoading,
    isPromotionsLoading,
    isVariantLoading,
    product,
    productSpecialOffers,
    promotions,
    rightSidebarSizeProps,
    stickyProps,
    onAddToCartClick,
    onClickEngraving,
    onHoverChange
}) => {
    const {site} = useMultiSite()
    const [modalProduct, setModalProduct] = useState()
    const {isOpen, onClose} = useDisclosure({
        isOpen: modalProduct != null,
        onClose: () => setModalProduct(null)
    })
    // use updated product from products in case it was updated
    const updatedModalProduct = useMemo(() => {
        if (!modalProduct) {
            return null
        }
        return product.setProducts.find(
            (product) => product.setProductId === modalProduct.setProductId
        )
    }, [modalProduct, product])
    const {
        isEngravable,
        quantity,
        minOrderQuantity,
        maxOrderQuantity,
        stepQuantity,
        onChangeQuantity,
        isOrderable
    } = derivedProduct
    const onTileClick = useCallback((product) => {
        setModalProduct(product)
        trackPsdpClickProductTile(product)
    }, [])
    const {currency, displayPrice} = useProductPricing(product) || {}

    return (
        <Stack
            data-testid={'product-set-cockpit-right-column'}
            spacing={4}
            px={{base: 4, lg: 0}}
            scrollMarginTop={24}
            {...stickyProps}
            {...rightSidebarSizeProps}
        >
            {/* Frame 3319 */}
            <Stack spacing={4}>
                {/*Set Total*/}
                <ProductSetCockpitTotal
                    isLoading={isPromotionsLoading}
                    product={product}
                    display={{base: 'none', lg: 'flex'}}
                />

                <Divider display={{base: 'none', lg: 'block'}} borderColor="stokkeGray.dark" />

                <Stack spacing={2}>
                    <Text as={'h2'} textStyle="Regular XLarge">
                        <FormattedMessage
                            id="product_set_detail.cockpit.title"
                            defaultMessage="Make your set unique!"
                        />
                    </Text>
                    <Text textStyle="Regular Small">
                        <FormattedMessage
                            id="product_set_detail.cockpit.description"
                            defaultMessage="Choose different combinations by clicking on the tile."
                        />
                    </Text>
                </Stack>

                <ProductSetModal isOpen={isOpen} product={updatedModalProduct} onClose={onClose} />
                {product.setProducts.map((childProduct, index) => (
                    <Box key={childProduct.id + '_' + index} data-testid="child-product">
                        <ProductSetTile
                            product={childProduct}
                            onClick={onTileClick}
                            onHoverChange={onHoverChange}
                        />
                    </Box>
                ))}
            </Stack>

            <Stack spacing={{base: 4, lg: 6}} pt={{lg: 2}}>
                {!site?.isContentOnlySite && isEngravable && (
                    <Button variant="outline" size="lg" onClick={onClickEngraving}>
                        <FormattedMessage
                            id="product_set_detail.cockpit.add_all_to_basket_incl_engraving"
                            defaultMessage="Add all to basket incl. engraving"
                        />
                    </Button>
                )}

                <ProductSetCockpitTotal isLoading={isPromotionsLoading} product={product} />
            </Stack>

            {/* Special offer on mobile */}
            <Skeleton isLoaded={!isPromotionsLoading && !isVariantLoading}>
                <Box display={{base: 'block', lg: 'none'}}>
                    <ProductSpecialOffers {...productSpecialOffers} />
                </Box>
            </Skeleton>

            {/* Bottom */}
            <Flex flexDirection="column" gap={4}>
                <PayLater
                    amount={displayPrice}
                    currency={currency}
                    paypalPlacement="product"
                    klarnaPlacement="credit-promotion-badge"
                />
                <Flex flexDirection="column" gap={3}>
                    {isOrderable && !site?.isContentOnlySite && (
                        <QuantityPicker
                            containerProps={{
                                // matches the button style
                                maxWidth: {md: '25rem'}
                            }}
                            btnWidth={15}
                            height={14}
                            minQuantity={minOrderQuantity}
                            maxQuantity={maxOrderQuantity}
                            quantity={quantity}
                            quantityStep={stepQuantity}
                            onChange={onChangeQuantity}
                        />
                    )}
                    <AddToCartCTA
                        isLoading={isGetPropsLoading || isPromotionsLoading || isVariantLoading}
                        product={product}
                        quantity={quantity}
                        onClick={onAddToCartClick}
                    />
                    <CompareCta product={product} />
                    {!site?.isContentOnlySite && <WhereToBuyCTA product={product} />}
                </Flex>

                <CockpitOrderSection
                    derivedProduct={derivedProduct}
                    product={product}
                    productSpecialOffers={productSpecialOffers}
                    promotions={promotions}
                    isGetPropsLoading={isGetPropsLoading}
                    isPromotionsLoading={isPromotionsLoading}
                    isProductASet
                    isVariantLoading={isVariantLoading}
                    onAddToCartClick={onAddToCartClick}
                />
            </Flex>
        </Stack>
    )
}

PsdpCockpitRightColumn.propTypes = {
    derivedProduct: PropTypes.object,
    isGetPropsLoading: PropTypes.bool,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    product: PropTypes.object,
    productSpecialOffers: PropTypes.object,
    promotions: PropTypes.array,
    rightSidebarSizeProps: PropTypes.object,
    setProductProps: PropTypes.object,
    stickyProps: PropTypes.object,
    onAddToCartClick: PropTypes.func,
    onClickEngraving: PropTypes.func,
    onHoverChange: PropTypes.func
}

export default PsdpCockpitRightColumn
